import { IAngularEvent, IPromise, IQService, IScope } from "angular";
import { ICheckoutPageData, ISubscriptionConverted } from "@gtmhub/login/models";
import { IAccountSubscription } from "../models";
import { AccountResolverService } from "@gtmhub/state/accountResolverService";
import { AccountService } from "@gtmhub/accounts/accountsService";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { ChargebeeService } from "../services/chargebeeService";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { IUser } from "@gtmhub/users/models";
import { IStateInit } from "@gtmhub/core/routing";
import { AccountType, IAccount, ILicense } from "@gtmhub/core";
import { hasCurrentUserRole } from "@gtmhub/users";
import { IPlan, IPlanLabel, PricingEditionService, getPlanLabel, isAccountWithBasicEdition } from "@gtmhub/edition-plan-change";
import { UserInvitationModalService } from "@gtmhub/users/components/user-invitation-form/user-invitation-modal.service";

export interface IBillingScope extends IScope {
  account: IAccount;
  accountSubscription: IAccountSubscription;
  indicators: {
    loading: boolean;
    openingCheckout: boolean;
  };

  planLabel: IPlanLabel;
  isAdmin: boolean;
  accountEditionPermissions: boolean;
  isAccountWithBasicEdition: boolean;
  isAccountTrial: boolean;
  isAccountClient: boolean;

  contactSales(): void;
  openPortalSection(sectionName: string): void;
  openCheckout(): void;
  openInvitationModal(): void;
}

export class BillingCtrl implements IStateInit {
  private plans: IPlan[];
  private selectedPlan: IPlan;

  public static $inject = [
    "$scope",
    "$q",
    "AccountResolverService",
    "AccountService",
    "AnalyticsService",
    "ChargebeeService",
    "UIErrorHandlingService",
    "PricingEditionService",
    "UserInvitationModalService",
  ];

  constructor(
    private $scope: IBillingScope,
    private $q: IQService,
    private accountResolverService: AccountResolverService,
    private accountService: AccountService,
    private analyticsService: AnalyticsService,
    private chargebeeService: ChargebeeService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private pricingEditionService: PricingEditionService,
    private userInvitationModal: UserInvitationModalService
  ) {
    this.$scope.indicators = {
      loading: true,
      openingCheckout: false,
    };

    this.$scope.contactSales = this.contactSales;
    this.$scope.openPortalSection = this.openPortalSection;
    this.$scope.openCheckout = this.openCheckout;
    this.$scope.openInvitationModal = this.openInvitationModal;

    this.$scope.isAdmin = hasCurrentUserRole("admin");

    this.$scope.$on("invitedUsers", this.onInvitedUsers);
    this.$scope.$on("cancelSubscription", this.onCancelSubscription);
    this.$scope.$on("subscriptionConverted", this.onSubscriptionConverted);
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private init(): IPromise<unknown> {
    this.getAccount();

    return this.$q
      .all({
        accountSubscription: this.accountService.getAccountSubscription(),
        plans: this.pricingEditionService.getAllowedTransitions(this.$scope.account.type),
      })
      .then(
        ({ accountSubscription, plans }) => {
          this.$scope.accountSubscription = accountSubscription;
          this.plans = plans;
          this.$scope.accountEditionPermissions = !!this.plans.length;

          if (this.$scope.accountEditionPermissions) {
            this.setPlanUIVariables();
          }

          this.$scope.indicators.loading = false;
        },
        (error) => this.uiErrorHandlingService.handleModal(error)
      );
  }

  private contactSales = (): void => {
    this.analyticsService.contactSales("contactSales");
  };

  private getAccount = () => {
    this.$scope.account = this.accountResolverService.getAccountData();
    let isAccountConverted: boolean;

    if (this.$scope.account.subscriptions.length) {
      isAccountConverted = this.$scope.account.subscriptions.find((license) => license.type === "regular").isConverted;
    }

    this.$scope.isAccountTrial = this.$scope.account.type === AccountType.TrialAccount && !isAccountConverted;
    this.$scope.isAccountClient = this.$scope.account.type === AccountType.ClientAccount || isAccountConverted;
  };

  private openPortalSection = (sectionName: string) => {
    this.chargebeeService.openPortalSection(sectionName);
  };

  private openCheckout = () => {
    this.$scope.indicators.openingCheckout = true;

    this.accountService
      .getCheckoutPage({ billingPeriod: this.selectedPlan.billingPeriod, planGroupName: this.selectedPlan.planGroupName, edition: this.selectedPlan.editionName })
      .then(
        (data: ICheckoutPageData) => {
          this.chargebeeService.openCbCheckout(data.url);
          this.$scope.indicators.openingCheckout = false;
        },
        (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "openingCheckout", inline: true })
      );
  };

  private setPlanUIVariables = () => {
    this.selectedPlan = this.plans.find((plan) => plan.isSelected);
    this.$scope.planLabel = getPlanLabel(this.selectedPlan.id);
    this.$scope.isAccountWithBasicEdition = isAccountWithBasicEdition(this.plans);
  };

  private onInvitedUsers = (event: IAngularEvent, invitedUsers: IUser[]) => {
    this.$scope.accountSubscription.items.forEach((license: ILicense) => {
      const usersCount = invitedUsers.filter((user: IUser) => user.subscriptionType === license.type).length;

      license.usersCount += usersCount;

      if (license.usersCount > license.usersAllowed) {
        license.usersAllowed = license.usersCount;
      }
    });
  };

  private onCancelSubscription = () => {
    this.$scope.accountSubscription.allowedCancellation = false;
    this.$scope.accountSubscription.hasScheduledCancellation = true;
  };

  private onSubscriptionConverted = (event: IAngularEvent, subscriptionConverted: ISubscriptionConverted): void => {
    if (this.$scope.account.id === subscriptionConverted.accountId) {
      this.$scope.account.edition = subscriptionConverted.accountEdition;

      const subscriptionTypeRegular = subscriptionConverted.subscriptions.find((license) => license.type === "regular");
      if (subscriptionTypeRegular) {
        this.$scope.isAccountTrial = !subscriptionTypeRegular.isConverted;
        this.$scope.isAccountClient = subscriptionTypeRegular.isConverted;
      }

      this.plans = this.plans.map((plan) => ({ ...plan, isSelected: plan.id === subscriptionConverted.accountEdition.planId }));
      this.setPlanUIVariables();
    }
  };

  private openInvitationModal = () => {
    this.userInvitationModal.open();
  };
}
